import React, { useState, useEffect, useRef } from "react";
import { clsx } from "clsx";
import { motion, AnimatePresence } from "framer-motion";
import ReactionButton from "./ReactionButton";
import ReactionSheet from "./ReactionSheet";
import ReactionUserModal from "@/pages/LiveVariant/components/Social/ReactionUserModal";
import { LuSmilePlus } from "react-icons/lu";
import useReactions from "@/hooks/useReactions";
import { REACTION_EMOJIS } from "@/constants/reaction-unicode";
import { useUser } from "@/context/UserContext";

const REACTION_ORDER = REACTION_EMOJIS;

interface ReactionBarProps {
    propertyId: string;
    className?: string;
    pageName?: string;
}

const ReactionBar: React.FC<ReactionBarProps> = ({ propertyId, className = "", pageName = "" }) => {
    const [showSheet, setShowSheet] = useState(false);
    const [showUserModal, setShowUserModal] = useState(false);
    const [selectedReaction, setSelectedReaction] = useState("all");
    const [showBounce, setShowBounce] = useState(false);
    const addButtonRef = useRef<HTMLButtonElement>(null);
    const { userState } = useUser();

    const { reactions, userReactions, handleReactionToggle } = useReactions(
        "Property",
        propertyId,
        userState,
        pageName,
    );

    // Show bounce animation after 5 seconds if no reactions exist
    useEffect(() => {
        const hasAnyReactions = Object.values(reactions).some((count) => count > 0);

        if (!hasAnyReactions) {
            const timer = setTimeout(() => {
                setShowBounce(true);
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [reactions]);

    const handleExistingReactionClick = (reaction: string, toggleSource: string) => {
        handleReactionToggle(reaction, toggleSource);
    };

    const handleReactionLongPress = (reaction: string) => {
        setSelectedReaction(reaction);
        setShowUserModal(true);
    };

    // Sort reactions from most to least, using REACTION_ORDER as tiebreaker
    const orderedReactions: [string, number][] = REACTION_ORDER.map(
        (emoji) => [emoji, reactions[emoji] || 0] as [string, number],
    )
        .filter(([, count]) => count > 0)
        .sort((a, b) => {
            if (b[1] !== a[1]) return b[1] - a[1]; // Sort by count descending
            return REACTION_ORDER.indexOf(a[0]) - REACTION_ORDER.indexOf(b[0]); // Tiebreaker: original order
        });

    const hasAnyReactions = orderedReactions.length > 0;

    return (
        <>
            <div className={clsx("tw-flex tw-items-center tw-gap-2 tw-relative tw-z-[10]", className)}>
                {/* Add Reaction Button */}
                <button
                    ref={addButtonRef}
                    onClick={() => setShowSheet(true)}
                    className="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-px-2 tw-h-[2em] tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-full tw-transition-all tw-duration-200 tw-text-sm tw-font-medium tw-text-gray-700"
                >
                    <LuSmilePlus className="tw-text-lg" />
                </button>
                {!hasAnyReactions && (
                    <motion.span className="tw-font-medium">
                        {"Be the first to react".split("").map((char, index) => (
                            <motion.span
                                key={index}
                                animate={
                                    showBounce
                                        ? {
                                              y: [0, -6, 0],
                                              transition: {
                                                  duration: 0.6,
                                                  repeat: Infinity,
                                                  repeatDelay: 10,
                                                  delay: index * 0.05, // Wave effect - each letter animates 0.05s after the previous
                                              },
                                          }
                                        : {}
                                }
                                style={{ display: "inline-block" }}
                            >
                                {char === " " ? "\u00A0" : char}
                            </motion.span>
                        ))}
                    </motion.span>
                )}

                {/* Existing Reactions */}
                <AnimatePresence>
                    {orderedReactions.map(([reaction, count]) => (
                        <motion.div
                            key={reaction}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <ReactionButton
                                reaction={reaction}
                                count={count}
                                isUserReacted={userReactions.includes(reaction)}
                                onClick={() => handleExistingReactionClick(reaction, "reaction_bar")}
                                onLongPress={() => handleReactionLongPress(reaction)}
                            />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>

            {/* Reaction Sheet */}
            <ReactionSheet
                isOpen={showSheet}
                onClose={() => setShowSheet(false)}
                onReactionSelect={handleReactionToggle}
                userReactions={userReactions}
                anchorEl={addButtonRef.current}
            />

            {/* User Modal */}
            <ReactionUserModal
                show={showUserModal}
                onClose={() => setShowUserModal(false)}
                propertyId={propertyId}
                initialEmoji={selectedReaction}
            />
        </>
    );
};

export default ReactionBar;
